'use client';

import { Code } from '@/sanity.types';
import DynamicScriptLoader from '@/components/DynamicScriptLoader';
import DynamicExternalScriptLoader from '@/components/DynamicExternalScriptLoader';
import { useEffect, useState } from 'react';

interface ScriptHandlerProps {
  jsCode: Code;
}

const ScriptHandler = ({ jsCode }: ScriptHandlerProps) => {
  const [externalScripts, setExternalScripts] = useState<string[]>([]);
  const [inlineCode, setInlineCode] = useState<string | null>(null);

  useEffect(() => {
    if (!jsCode || !jsCode.code) {
      return;
    }

    // Function to extract script URLs from the code
    const extractScriptUrls = (code: string): string[] => {
      const scriptRegex = /<script[^>]*src=["']([^"']+)["'][^>]*><\/script>/gi;
      const urls: string[] = [];

      let match: RegExpExecArray | null;
      while ((match = scriptRegex.exec(code)) !== null) {
        if (match[1]) {
          urls.push(match[1]);
        }
      }

      return urls;
    };

    // Function to remove script tags with src attributes
    const removeExternalScriptTags = (code: string): string => {
      return code.replace(
        /<script[^>]*src=["'][^"']+["'][^>]*><\/script>/gi,
        ''
      );
    };

    const urls = extractScriptUrls(jsCode.code);
    setExternalScripts(urls);

    // Remove external script tags and keep only inline code
    const cleanedCode = removeExternalScriptTags(jsCode.code);

    // If there's still some code left after removing external scripts, save it
    if (cleanedCode.trim()) {
      setInlineCode(cleanedCode);
    } else {
      setInlineCode(null);
    }
  }, [jsCode]);

  return (
    <>
      {/* Handle external scripts */}
      {externalScripts.map((scriptUrl, index) => (
        <DynamicExternalScriptLoader
          key={`ext-script-${index}`}
          scriptUrl={scriptUrl}
        />
      ))}

      {/* Handle inline JavaScript */}
      {inlineCode && (
        <DynamicScriptLoader js={{ _type: 'code', code: inlineCode }} />
      )}
    </>
  );
};

export default ScriptHandler;
